import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList, Cell
} from 'recharts';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import Loader from '../components/Loader';
import { fetchCoverage } from '../components/EntryFlowModal';
import { X } from '../components/v2/Icons';
import {
  PageHeader, Card, Button, Stat, Badge, Callout, Empty, Skeleton, Select, BarRow, Delta,
} from '../components/v2/ui';
import { Donut, MonthlyBars, SCOPE_COLORS } from '../components/v2/ui/Charts';
import { useT } from '../i18n';
import './EmissionsDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

// Format number with commas
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-US', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Chart.js plugin: draw dashed grid lines on the y-axis
const renderBarLabel = (props) => {
  const { x, y, width, value } = props;
  if (!value || value === 0) return null;
  return (
    <text x={x + width / 2} y={y - 10} fill="#6b7280" textAnchor="middle" fontSize={11} fontWeight={500}>
      {value >= 1000 ? `${Math.round(value)} tCo2e` : `${value.toFixed(0)} tCo2e`}
    </text>
  );
};

// Scope Card Component - Figma style
const ScopeCard = ({ title, total, delta, deltaPositive, deltaAmount, data, dataKeys, colors, labels, dropdownValue, onDropdownChange }) => {
  const { t } = useT();
  return (
  <Card
    title={title}
    sub={t('reports.stackedBy')}
    right={
      <Select value={dropdownValue} onChange={onDropdownChange} style={{ width: 'auto' }}>
        <option value="8">{t('reports.last8')}</option>
        <option value="12">{t('reports.last12')}</option>
        <option value="all">{t('reports.allTime')}</option>
      </Select>
    }
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
      <span className="k2-num" style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.026em' }}>
        {formatNumber(total, 2)}
      </span>
      <span style={{ fontSize: 12, color: 'var(--n500)' }}>{t('common.tco2e')}</span>
      <span style={{ marginLeft: 'auto' }}><Delta v={deltaPositive ? Math.abs(delta) : -Math.abs(delta)} /></span>
    </div>
    <div style={{ fontSize: 11.5, color: 'var(--n500)', marginBottom: 14 }}>
      {t('reports.vsLastYear', { sign: deltaPositive ? '+' : '−', value: formatNumber(Math.abs(deltaAmount), 2) })}
    </div>

    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
      {labels.map((label, idx) => (
        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--n600)' }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: colors[idx], display: 'inline-block' }} />
          {label}
        </span>
      ))}
    </div>

    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} barCategoryGap="10%" margin={{ top: 26, right: 10, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="var(--n100)" />
        <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--n400)' }} axisLine={false} tickLine={false} />
        <YAxis hide />
        <Tooltip
          formatter={(value) => [`${formatNumber(value)} tCO₂e`, '']}
          contentStyle={{ borderRadius: 8, border: '1px solid var(--n200)', fontSize: 12 }}
          cursor={{ fill: 'var(--k2-hover)' }}
        />
        {dataKeys.map((key, idx) => (
          <Bar key={key} dataKey={key} stackId="a" fill={colors[idx]} stroke="#fff" strokeWidth={4} radius={idx === dataKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}>
            {idx === dataKeys.length - 1 && <LabelList dataKey="total" content={renderBarLabel} />}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  </Card>
  );
};

/* Biogenic CO₂ — a peer of Scope 1/2/3, never inside Scope 1. Under the GHG
   Protocol and ISO 14064 it is reported in parallel for transparency, so it gets
   its own card rather than a footnote. */
export const BiogenicCard = ({ total, items }) => {
  const { t } = useT();
  return (
  <Card
    title={t('reports.biogenic')}
    sub={t('reports.biogenicSub')}
    right={<Badge tone="ok">{t('reports.parallel')}</Badge>}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
      <span className="k2-num" style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.026em' }}>
        {formatNumber(total, 3)}
      </span>
      <span style={{ fontSize: 12, color: 'var(--n500)' }}>{t('common.tco2e')}</span>
    </div>

    {items && items.length > 0 && (
      <table className="k2-dt2">
        <thead>
          <tr>
            <th>{t('reports.fuel')}</th>
            <th className="k2-r">{t('reports.records')}</th>
            <th className="k2-r">{t('reports.quantity')}</th>
            <th className="k2-r">tCO₂e</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it.key}>
              <td>{it.label}</td>
              <td className="k2-r" style={{ color: 'var(--n400)' }}>{it.count}</td>
              <td className="k2-r" style={{ color: 'var(--n500)' }}>
                {it.quantity ? `${formatNumber(it.quantity, 2)} ${it.unit || ''}` : '—'}
              </td>
              <td className="k2-r" style={{ fontWeight: 500 }}>{formatNumber(it.emissions_tCO2e, 3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Card>
  );
};

/** Collapsible per-category breakdown. Closed by default. */
export const BreakdownAccordion = ({ title, total, items, defaultOpen = false }) => {
  const { t } = useT();
  const [open, setOpen] = useState(defaultOpen);
  if (!items || items.length === 0) return null;
  const expanded = open || defaultOpen;

  return (
    <div className="k2-c">
      <button type="button" className="k2-acc" onClick={() => setOpen(o => !o)} aria-expanded={expanded}>
        <span className="k2-acc-t">{title}</span>
        <span className="k2-acc-v">{formatNumber(total, 3)} tCO₂e</span>
        <span className={`k2-acc-c${expanded ? ' k2-on' : ''}`}><X.ChevD s={13} /></span>
      </button>
      {expanded && (
        <div className="k2-c-b" style={{ paddingTop: 0 }}>
          <table className="k2-dt2">
            <tbody>
              {items.map(it => (
                <tr key={it.key}>
                  <td>{it.label}</td>
                  <td className="k2-r" style={{ color: 'var(--n400)', whiteSpace: 'nowrap' }}>
                    {it.count} {t('reports.records').toLowerCase()}
                  </td>
                  <td className="k2-r" style={{ color: 'var(--n500)', whiteSpace: 'nowrap' }}>
                    {it.quantity ? `${formatNumber(it.quantity, 2)} ${it.unit || ''}` : ''}
                  </td>
                  <td className="k2-r" style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {formatNumber(it.emissions_tCO2e, 3)} t
                    {total > 0 && (
                      <span style={{ marginLeft: 6, color: 'var(--n400)', fontWeight: 400 }}>
                        ({((it.emissions_tCO2e / total) * 100).toFixed(0)}%)
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* The eight bespoke KPI cards (KPI_CARD_STYLE, TrendBadge, KpiCardTotal and
   siblings) are gone: `Stat` from the v2 design system renders all of them,
   including the delta chip, and it already treats a RISE in emissions as bad.
   The figures and their sources are unchanged. */

const EmissionsDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  /* A bookmarked ?range=last-3-months would otherwise select an option that no
     longer exists, leaving the control blank and sending a dead range to the API. */
  const [range, setRange] = useState(() => {
    const r = searchParams.get('range');
    return ['last-month', 'last-year', 'all-time'].includes(r) ? r : 'last-year';
  });
  const [scopeFilter, setScopeFilter] = useState(searchParams.get('scope') || 'all');
  const [buildingFilter, setBuildingFilter] = useState(searchParams.get('building') || '');
  const [buildings, setBuildings] = useState([]);
  const [scope1Range, setScope1Range] = useState('8');
  const [scope2Range, setScope2Range] = useState('8');
  const [exporting, setExporting] = useState(false);
  // Report parity: annual summary from the same assembler the PDF uses
  const [reportSummary, setReportSummary] = useState(null);
  // Missing-data banner for the previous month (dismissible per month)
  const [bannerCov, setBannerCov] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(true);
  // Company logo shown on the PDF report cover
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef(null);
  // CSV Export Function (respects scope filter)
  const handleExportCSV = () => {
    if (!data) return;
    
    const { metrics, breakdown, byScope, mostEmissions, scope1, scope2, scope3, detailedBreakdown } = data;
    const sf = scopeFilter;
    const showS1 = sf === 'all' || sf === 'scope1';
    const showS2 = sf === 'all' || sf === 'scope2';
    const showS3 = sf === 'all' || sf === 'scope3';
    
    let csv = 'Section,Metric,Value,Unit,Delta\n';
    
    // Metrics
    csv += `Metrics,Total Emissions,${metrics.totalEmissions.value},${metrics.totalEmissions.unit},${metrics.totalEmissions.deltaPositive ? '+' : ''}${metrics.totalEmissions.delta}%\n`;
    csv += `Metrics,Number of Employees,${metrics.employeeCount.value},,${metrics.employeeCount.deltaPositive ? '+' : ''}${metrics.employeeCount.delta}%\n`;
    csv += `Metrics,Emission per Employee,${metrics.emissionPerEmployee.value},${metrics.emissionPerEmployee.unit},${metrics.emissionPerEmployee.deltaPositive ? '+' : ''}${metrics.emissionPerEmployee.delta}%\n`;
    csv += `Metrics,Facility Average,${metrics.facilityAverage.value},${metrics.facilityAverage.unit},${metrics.facilityAverage.deltaPositive ? '+' : ''}${metrics.facilityAverage.delta}%\n`;
    
    csv += '\n';
    
    // Emissions by Scope (only when showing all)
    if (sf === 'all') {
      csv += 'Emissions by Scope,Scope,Percentage\n';
      byScope.forEach(s => {
        csv += `Emissions by Scope,${s.name},${s.value}%\n`;
      });
      csv += '\n';
    }
    
    // Breakdown by Month
    csv += 'Monthly Breakdown,Month,Target,Current\n';
    breakdown.forEach(b => {
      csv += `Monthly Breakdown,${b.month},${b.target},${b.current}\n`;
    });
    
    csv += '\n';
    
    // Most Emissions
    csv += 'Most Emissions,Source,Value,Percentage\n';
    mostEmissions.forEach(e => {
      csv += `Most Emissions,${e.name},${e.value},${e.percentage}%\n`;
    });
    
    csv += '\n';
    
    // Scope 1 Yearly
    if (showS1) {
      csv += 'Scope 1 Yearly,Year,Stationary Combustion,Mobile Combustion,Process Emissions,Total\n';
      scope1.yearly.forEach(y => {
        csv += `Scope 1 Yearly,${y.year},${y.djegjaStacionare || 0},${y.djegjaMobile || 0},${y.emetimetNgaProceset || 0},${y.total || 0}\n`;
      });
      if ((scope1.biogenicMemo || 0) > 0) {
        csv += `Biogenic CO2 (Parallel Reporting),Total — reported for transparency NOT included in Scope 1,${scope1.biogenicMemo}\n`;
      }
      csv += '\n';
    }
    
    // Scope 2 Yearly
    if (showS2) {
      csv += 'Scope 2 Yearly,Year,Electricity,Heating,EV Charging,Total\n';
      scope2.yearly.forEach(y => {
        csv += `Scope 2 Yearly,${y.year},${y.elektriciteti || 0},${y.nxemja || 0},${y.veturatElektrike || 0},${y.total || 0}\n`;
      });
      csv += '\n';
    }
    
    // Scope 3 Yearly
    if (showS3) {
      csv += 'Scope 3 Yearly,Period,Value\n';
      scope3.yearly.forEach(y => {
        csv += `Scope 3 Yearly,${y.year},${y.value}\n`;
      });
    }

    // Detailed per-category breakdowns
    if (detailedBreakdown) {
      const csvEsc = (s) => {
        const v = String(s ?? '');
        return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      };
      if (showS1 && detailedBreakdown.scope1) {
        csv += '\nDetailed Scope 1 - Stationary,Fuel,Quantity,Unit,Records,Emissions tCO2e\n';
        detailedBreakdown.scope1.stationary.items.forEach(it => {
          csv += `,${csvEsc(it.label)},${it.quantity},${csvEsc(it.unit || '')},${it.count},${it.emissions_tCO2e}\n`;
        });
        csv += '\nDetailed Scope 1 - Mobile,Vehicle/Fuel,Quantity,Unit,Records,Emissions tCO2e\n';
        detailedBreakdown.scope1.mobile.items.forEach(it => {
          csv += `,${csvEsc(it.label)},${it.quantity},${csvEsc(it.unit || '')},${it.count},${it.emissions_tCO2e}\n`;
        });
        csv += '\nDetailed Scope 1 - Process,Type,Quantity,Unit,Records,Emissions tCO2e\n';
        detailedBreakdown.scope1.process.items.forEach(it => {
          csv += `,${csvEsc(it.label)},${it.quantity},${csvEsc(it.unit || '')},${it.count},${it.emissions_tCO2e}\n`;
        });
        if (detailedBreakdown.scope1.biogenicMemo.total > 0) {
          csv += '\nBiogenic CO2 — Parallel Reporting (NOT included in Scope 1),Fuel,Quantity,Unit,Records,Emissions tCO2e\n';
          detailedBreakdown.scope1.biogenicMemo.items.forEach(it => {
            csv += `,${csvEsc(it.label)},${it.quantity},${csvEsc(it.unit || '')},${it.count},${it.emissions_tCO2e}\n`;
          });
        }
      }
      if (showS2 && detailedBreakdown.scope2) {
        csv += '\nDetailed Scope 2,Type,Quantity,Unit,Records,Emissions tCO2e\n';
        detailedBreakdown.scope2.items.forEach(it => {
          csv += `,${csvEsc(it.label)},${it.quantity},${csvEsc(it.unit || '')},${it.count},${it.emissions_tCO2e}\n`;
        });
      }
      if (showS3 && detailedBreakdown.scope3) {
        csv += '\nDetailed Scope 3,Category,Activity,Quantity,Unit,Records,Emissions tCO2e\n';
        detailedBreakdown.scope3.categories.forEach(cat => {
          cat.items.forEach(it => {
            csv += `,${csvEsc(cat.label)},${csvEsc(it.label)},${it.quantity},${csvEsc(it.unit || '')},${it.count},${it.emissions_tCO2e}\n`;
          });
        });
      }
    }

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const scopeLabel = sf === 'all' ? '' : `_${sf}`;
    link.download = `emissions_report${scopeLabel}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // PDF Export — downloads the server-rendered GHG Inventory Report
  // (headless-Chrome render of the same template shown at /emissions/report)
  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/api/report/pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        // The report is always a calendar-year inventory; the building filter
        // narrows its organizational boundary to the selected facility.
        body: JSON.stringify(buildingFilter ? { buildingId: Number(buildingFilter) } : {})
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : 'GHG-Report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Fetch buildings list once
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/buildings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => setBuildings(data))
      .catch(() => setBuildings([]));
  }, []);

  // Annual report summary (same numbers as the PDF export)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const params = new URLSearchParams();
    if (buildingFilter) params.set('buildingId', buildingFilter);
    fetch(`${API_BASE}/api/report/data?${params.toString()}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(setReportSummary)
      .catch(() => setReportSummary(null));
  }, [buildingFilter]);

  // Previous-month coverage → missing-data banner
  const prevPeriod = useMemo(() => {
    const p = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
    return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setBannerDismissed(localStorage.getItem(`k360-banner-${prevPeriod}`) === '1');
    fetchCoverage(prevPeriod)
      .then(setBannerCov)
      .catch(() => setBannerCov(null));
  }, [prevPeriod]);

  // Current company logo (rendered on the PDF report cover)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/profile/logo`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : { logo_url: null })
      .then(d => setLogoUrl(d.logo_url || null))
      .catch(() => {});
  }, []);

  const handleLogoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setLogoBusy(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('logo', file);
      const r = await fetch(`${API_BASE}/api/profile/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`);
      setLogoUrl(d.logo_url);
    } catch (err) {
      console.error('Logo upload failed:', err);
      alert(err.message || t('dashp.logoUploadFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/api/profile/logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setLogoUrl(null);
    } catch (err) {
      console.error('Logo remove failed:', err);
      alert(t('dashp.logoRemoveFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/';
      return;
    }

    const params = new URLSearchParams();
    params.set('range', range);
    params.set('scope1Range', scope1Range);
    params.set('scope2Range', scope2Range);
    if (buildingFilter) params.set('buildingId', buildingFilter);

    fetch(`${API_BASE}/api/dashboard?${params.toString()}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (res.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/';
          return Promise.reject();
        }
        return res.ok ? res.json() : Promise.reject();
      })
      .then(data => {
        setData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Dashboard fetch error:', err);
        setLoading(false);
      });
  }, [range, scope1Range, scope2Range, buildingFilter]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    if (scopeFilter && scopeFilter !== 'all') params.set('scope', scopeFilter);
    if (buildingFilter) params.set('building', buildingFilter);
    setSearchParams(params, { replace: true });
  }, [range, scopeFilter, buildingFilter, setSearchParams]);

  if (loading) {
    return (
      <ErrorBoundary>
        <Shell>
          <div className="k2-wrap">
            <PageHeader title={t('reports.title')} desc={t('reports.loading')} />
            <div className="k2-g4" style={{ marginBottom: 24 }}>
              {[0, 1, 2, 3].map((i) => <Card key={i}><Skeleton h={54} /></Card>)}
            </div>
            <Card><Skeleton h={240} /></Card>
          </div>
        </Shell>
      </ErrorBoundary>
    );
  }

  if (!data) {
    return (
      <ErrorBoundary>
        <Shell>
          <div className="k2-wrap">
            <PageHeader title={t('reports.title')} />
            <Card>
              <Empty
                icon={X.Alert}
                title={t('reports.loadFail')}
                body={t('reports.loadFailBody')}
                action={<Button variant="mint" onClick={() => window.location.reload()}>{t('common.retry')}</Button>}
              />
            </Card>
          </div>
        </Shell>
      </ErrorBoundary>
    );
  }

  const { kpis, breakdown, scope1Breakdown, scope2Breakdown, scope3Breakdown, byScope, mostEmissions, scope1, scope2, scope3, detailedBreakdown } = data;

  // Get scope-filtered breakdown for the chart
  const getFilteredBreakdown = () => {
    if (scopeFilter === 'scope1') return scope1Breakdown || breakdown;
    if (scopeFilter === 'scope2') return scope2Breakdown || breakdown;
    if (scopeFilter === 'scope3') return scope3Breakdown || breakdown;
    return breakdown;
  };
  const filteredBreakdown = getFilteredBreakdown();

  // Filter mostEmissions by scope and recalculate percentages relative to filtered total
  const filteredMostEmissions = scopeFilter === 'all'
    ? mostEmissions
    : (() => {
        const filtered = mostEmissions.filter(item => item.scope === scopeFilter);
        const scopeTotal = filtered.reduce((sum, item) => sum + (item.value || 0), 0);
        return filtered.map(item => ({
          ...item,
          percentage: scopeTotal > 0 ? (item.value / scopeTotal) * 100 : 0
        }));
      })();

  // Visibility flags
  const showScope1 = scopeFilter === 'all' || scopeFilter === 'scope1';
  const showScope2 = scopeFilter === 'all' || scopeFilter === 'scope2';
  const showScope3 = scopeFilter === 'all' || scopeFilter === 'scope3';
  const showDonut = scopeFilter === 'all';

  /* Absolute tonnes, not the percentages `byScope` carries, so this donut reads
     the same as the one on Overview and its centre total reconciles with the
     KPI strip above it. */
  const donutData = [
    { name: t('overview.scope1'), value: scope1?.total || 0, color: SCOPE_COLORS.scope1 },
    { name: t('overview.scope2'), value: scope2?.total || 0, color: SCOPE_COLORS.scope2 },
    { name: t('overview.scope3'), value: scope3?.total || 0, color: SCOPE_COLORS.scope3 },
  ];

  const vb = kpis?.vsBaseline;
  const comp = kpis?.inventoryCompleteness;

  return (
    <ErrorBoundary>
      <Shell>
        <div className="k2-wrap">
          {exporting && <Loader />}

          <PageHeader
            title={t('reports.title')}
            desc={t('reports.desc')}
          >
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              style={{ display: 'none' }}
              onChange={handleLogoFile}
            />
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Company logo"
                title={t('reports.logoTitle')}
                style={{ height: 28, maxWidth: 84, objectFit: 'contain', border: '1px solid var(--n200)', borderRadius: 'var(--r-xs)', padding: 3, background: '#fff' }}
              />
            )}
            <Button
              variant="gh"
              icon={X.Upload}
              disabled={logoBusy}
              title={t('reports.logoTitle')}
              onClick={() => logoInputRef.current && logoInputRef.current.click()}
            >
              {logoBusy ? '…' : t(logoUrl ? 'reports.changeLogo' : 'reports.uploadLogo')}
            </Button>
            {logoUrl && (
              <Button variant="gh" iconOnly icon={X.Close} disabled={logoBusy} onClick={handleLogoRemove} aria-label={t('reports.removeLogo')} />
            )}
            <Button icon={X.Layers} onClick={handleExportCSV} disabled={exporting}>{t('reports.exportCsv')}</Button>
            <Button variant="mint" icon={X.Doc} onClick={handleExportPDF} disabled={exporting}>
              {t(exporting ? 'reports.exporting' : 'reports.exportPdf')}
            </Button>
          </PageHeader>

          {/* ── filters ── */}
          <div className="k2-fb" style={{ marginBottom: 20 }}>
            <Select value={range} onChange={(e) => setRange(e.target.value)} style={{ width: 'auto' }}>
              {/* 3- and 6-month windows removed 2026-07-30. The data scientist: in a GHG
                  inventory every comparison is BASELINE YEAR vs the current year, tracked
                  against the reduction target (2025 vs 2026, then 2025 vs 2027 … 2025 vs
                  2030). A rolling quarter has no meaning in that model. */}
              <option value="last-month">{t('reports.timeRange')}: {t('reports.range.lastMonth')}</option>
              <option value="last-year">{t('reports.timeRange')}: {t('reports.range.lastYear')}</option>
              <option value="all-time">{t('reports.timeRange')}: {t('reports.range.allTime')}</option>
            </Select>
            <Select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} style={{ width: 'auto' }}>
              <option value="all">{t('reports.scope')}: {t('common.all')}</option>
              <option value="scope1">Scope 1</option>
              <option value="scope2">Scope 2</option>
              <option value="scope3">Scope 3</option>
            </Select>
            <Select value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)} style={{ width: 'auto' }}>
              <option value="">{t('reports.facility')}: {t('common.all')}</option>
              {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
            {scopeFilter !== 'all' && (
              <Badge tone="info">{t('reports.filtered', { scope: scopeFilter.replace('scope', 'Scope ') })}</Badge>
            )}
          </div>

          {/* ── missing-data banner, previous month, dismissible ── */}
          {bannerCov && !bannerDismissed && (() => {
            const s1 = bannerCov.scope1.stationary + bannerCov.scope1.mobile + bannerCov.scope1.process;
            const s2 = bannerCov.scope2.electricity + bannerCov.scope2.heating + bannerCov.scope2.ev;
            if (s1 > 0 && s2 > 0) return null;
            const missing = [s1 === 0 && 'Scope 1', s2 === 0 && t('dashp.scope2Elec')].filter(Boolean).join(` ${t('dashp.and')} `);
            return (
              <div style={{ marginBottom: 20 }}>
                <Callout tone="warn" center>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span>{t('reports.bannerMissing', { period: prevPeriod, missing })}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <Button
                        size="sm"
                        variant="mint"
                        onClick={() => navigate(s2 === 0 ? '/scope-2/electricity' : '/scope-1/djegja-stacionare')}
                      >
                        {t('reports.bannerAdd')}
                      </Button>
                      <Button
                        size="sm"
                        variant="gh"
                        onClick={() => { localStorage.setItem(`k360-banner-${prevPeriod}`, '1'); setBannerDismissed(true); }}
                      >
                        {t('reports.bannerDismiss')}
                      </Button>
                    </span>
                  </div>
                </Callout>
              </div>
            );
          })()}

          {/* ── KPI strip ── */}
          {kpis && (
            <div className="k2-g4" style={{ marginBottom: 24 }}>
              <Stat
                tone="dark"
                label={`Total — ${kpis.totalCurrentPeriod?.period || 'current period'}`}
                value={formatNumber(kpis.totalCurrentPeriod?.value ?? 0, 2)}
                unit={t('common.tco2e')}
                delta={kpis.totalCurrentPeriod?.deltaPct}
                foot={kpis.totalCurrentPeriod?.comparisonLabel}
              />
              <Stat
                tone={vb?.onTrack ? 'mint' : undefined}
                label={`vs ${vb?.baselineYear ?? 'baseline'}`}
                value={vb?.vsBaselinePct === null || vb?.vsBaselinePct === undefined ? '—' : formatNumber(vb.vsBaselinePct, 1)}
                unit={vb?.vsBaselinePct === null || vb?.vsBaselinePct === undefined ? '' : '%'}
                foot={vb?.vsBaselinePct === null || vb?.vsBaselinePct === undefined
                  ? 'No baseline-year data to compare against'
                  : `${vb.onTrack ? 'On track' : 'Off track'} for ${vb.targetYear}`}
              />
              <Stat
                label={t('overview.kpi.intensity')}
                value={formatNumber(kpis.intensity?.value ?? 0, 1)}
                unit="kgCO₂e/m²"
                delta={kpis.intensity?.deltaPct}
                foot={kpis.intensity?.totalAreaSqm ? `${formatNumber(kpis.intensity.totalAreaSqm, 0)} m² floor area` : 'No floor area recorded'}
              />
              <Stat
                label={t('overview.kpi.completeness')}
                value={comp ? `${comp.tracked}` : '—'}
                unit={comp ? `/ ${comp.outOf} months` : ''}
                foot={comp ? `${comp.complete} complete · ${comp.partial} partial` : ''}
              />

              {/* Report parity — assembled by the same code that builds the PDF, so
                  a mismatch here would mean the report and the app disagree. */}
              {reportSummary && (
                <Stat
                  label={t('reports.kpi.inventory', { year: reportSummary.year })}
                  value={formatNumber(reportSummary.totals.grand, 2)}
                  unit={t('common.tco2e')}
                  delta={reportSummary.totals.vsBaselinePct}
                  foot={reportSummary.totals.vsBaselinePct === null
                    ? t('reports.kpi.firstYear', { year: reportSummary.baselineYear })
                    : t('reports.kpi.vsBaseline', { year: reportSummary.baselineYear })}
                />
              )}
              {reportSummary && (
                <Stat
                  label={t('reports.kpi.evidence', { year: reportSummary.year })}
                  value={reportSummary.evidenceCount}
                  unit={t('reports.kpi.evidenceUnit')}
                  foot={t('reports.kpi.evidenceFoot', { count: reportSummary.facilities, facilities: t(reportSummary.facilities === 1 ? 'overview.facility' : 'overview.facilities') })}
                />
              )}
              <Stat
                label={t('reports.kpi.scopeSplit')}
                value={`${formatNumber(byScope?.[0]?.value ?? 0, 0)}/${formatNumber(byScope?.[1]?.value ?? 0, 0)}/${formatNumber(byScope?.[2]?.value ?? 0, 0)}`}
                unit="%"
                foot={t('reports.kpi.scopeSplitFoot')}
              />
              {(kpis.biogenicMemo?.value || 0) > 0 && (
                <Stat
                  label={t('reports.kpi.biogenic')}
                  value={formatNumber(kpis.biogenicMemo.value, 3)}
                  unit={t('common.tco2e')}
                  foot={t('reports.kpi.biogenicFoot')}
                />
              )}
            </div>
          )}

          {/* ── trend + scope split ── */}
          <div className={showDonut ? 'k2-g64' : ''} style={{ marginBottom: 24 }}>
            <Card
              title={t('reports.breakdown')}
              sub={t('reports.breakdownSub')}
            >
              {filteredBreakdown && filteredBreakdown.length > 0 ? (
                <MonthlyBars data={filteredBreakdown} height={260} barColor={SCOPE_COLORS.scope2} />
              ) : (
                <Empty icon={X.Chart} title={t('reports.nothingToPlot')} body={t('reports.nothingToPlotBody')} />
              )}
            </Card>

            {showDonut && (
              <Card title={t('reports.byScope')} sub={t('reports.byScopeSub')}>
                {donutData.some(d => d.value > 0) ? (
                  <Donut data={donutData} />
                ) : (
                  <Empty icon={X.Chart} title={t('reports.noEmissions')} body={t('reports.noEmissionsBody')} />
                )}
              </Card>
            )}
          </div>

          {/* ── most emissions ── */}
          <div style={{ marginBottom: 24 }}>
            <Card title={t('reports.most')} sub={t('reports.mostSub')}>
              {filteredMostEmissions.length === 0 ? (
                <Empty icon={X.Search} title={t('reports.noSources')} body={t('reports.noSourcesBody')} />
              ) : (
                (() => {
                  const items = filteredMostEmissions.slice(0, 7);
                  const maxPct = Math.max(...items.map(i => i.percentage || 0), 1);
                  return items.map((item, idx) => (
                    <BarRow
                      key={idx}
                      name={item.name}
                      value={formatNumber(item.value, 2)}
                      unit={t('common.tco2e')}
                      pct={((item.percentage || 0) / maxPct) * 100}
                    />
                  ));
                })()
              )}
            </Card>
          </div>

          {/* ── per-scope detail ── */}
          {(showScope1 || showScope2) && (
            <div className={showScope1 && showScope2 ? 'k2-g2' : ''} style={{ marginBottom: 24, alignItems: 'start' }}>
              {showScope1 && (
                <div className="k2-gcol">
                  <ScopeCard
                    title={t('reports.scope1')}
                    total={scope1.total}
                    delta={scope1.delta}
                    deltaPositive={scope1.deltaPositive}
                    deltaAmount={scope1.deltaAmount}
                    data={scope1.yearly}
                    dataKeys={['djegjaStacionare', 'djegjaMobile', 'emetimetNgaProceset']}
                    colors={['rgba(209,213,219,0.55)', 'rgba(63,255,216,0.35)', '#3fffd8']}
                    labels={[t('dashp.stationary'), t('dashp.mobile'), t('invnp.tabProcess')]}
                    dropdownValue={scope1Range}
                    onDropdownChange={(e) => setScope1Range(e.target.value)}
                  />
                  {(scope1.biogenicMemo || 0) > 0 && (
                    <Callout tone="ok">
                      {t('reports.biogenicSeparate', { value: formatNumber(scope1.biogenicMemo, 3) })}
                    </Callout>
                  )}
                  {detailedBreakdown?.scope1 && (
                    <>
                      <BreakdownAccordion
                        title={t('dashp.stationaryByFuel')}
                        total={detailedBreakdown.scope1.stationary.total}
                        items={detailedBreakdown.scope1.stationary.items}
                      />
                      <BreakdownAccordion
                        title={t('dashp.mobileByVehicle')}
                        total={detailedBreakdown.scope1.mobile.total}
                        items={detailedBreakdown.scope1.mobile.items}
                      />
                      <BreakdownAccordion
                        title={t('dashp.processByType')}
                        total={detailedBreakdown.scope1.process.total}
                        items={detailedBreakdown.scope1.process.items}
                      />
                    </>
                  )}
                </div>
              )}

              {showScope2 && (
                <div className="k2-gcol">
                  <ScopeCard
                    title={t('reports.scope2')}
                    total={scope2.total}
                    delta={scope2.delta}
                    deltaPositive={scope2.deltaPositive}
                    deltaAmount={scope2.deltaAmount}
                    data={scope2.yearly}
                    dataKeys={['elektriciteti', 'nxemja', 'veturatElektrike']}
                    colors={['rgba(209,213,219,0.55)', 'rgba(63,255,216,0.35)', '#3fffd8']}
                    labels={[t('dashp.electricity'), t('dashp.heating'), t('dashp.evCharging')]}
                    dropdownValue={scope2Range}
                    onDropdownChange={(e) => setScope2Range(e.target.value)}
                  />
                  {detailedBreakdown?.scope2?.items?.length > 0 && (
                    <BreakdownAccordion
                      title={t('dashp.scope2ByType')}
                      total={scope2.total}
                      items={detailedBreakdown.scope2.items}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── biogenic CO₂, parallel reporting ── */}
          {(scope1?.biogenicMemo || 0) > 0 && (
            <div style={{ marginBottom: 24 }}>
              <BiogenicCard
                total={scope1.biogenicMemo}
                items={detailedBreakdown?.scope1?.biogenicMemo?.items || []}
              />
            </div>
          )}

          {/* ── scope 3 ── */}
          {showScope3 && (
            <div className="k2-gcol" style={{ marginBottom: 24 }}>
              <Card title={t('reports.scope3')} sub={t('reports.scope3Sub')}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={scope3.yearly} barCategoryGap="20%" margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="var(--n100)" />
                    <XAxis dataKey="year" tick={{ fontSize: 11, fill: 'var(--n400)' }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'var(--n400)' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => v >= 1000000 ? `${v / 1000000}M` : v >= 1000 ? `${v / 1000}K` : v}
                      width={45}
                    />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--n200)', fontSize: 12 }} cursor={{ fill: 'var(--k2-hover)' }} formatter={(v) => [`${formatNumber(v)} tCO₂e`, 'Emissions']} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={28} fill={SCOPE_COLORS.scope3}>
                      {(scope3.yearly || []).map((_, index) => <Cell key={index} fill={SCOPE_COLORS.scope3} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              {detailedBreakdown?.scope3?.categories?.map(cat => (
                <BreakdownAccordion
                  key={cat.key}
                  title={`Scope 3 — ${cat.label}`}
                  total={cat.emissions_tCO2e}
                  items={cat.items}
                />
              ))}
            </div>
          )}
        </div>
      </Shell>
    </ErrorBoundary>
  );
};

export default EmissionsDashboard;
